import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { Modal } from 'antd'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { type Control, useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { dictionariesQueries } from '@/api/dictionaries/dictionariesQueries'
import AtomButton from '@/components/Atoms/AtomButton/AtomButton'
import AtomFormInput from '@/components/Atoms/AtomFormInput/AtomFormInput'
import AtomSelect from '@/components/Atoms/AtomSelect/AtomSelect'
import HoverElement from '@/components/Atoms/HoverElement/HoverElement'
import ModalMessage from '@/components/Atoms/ModalMessage/ModalMessage'
import { PAYMENT_TYPES } from '@/constants'
import { usePasswordVisibility } from '@/hooks/ui/usePasswordVisibility'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type {
  FormInputClearErrorsType,
  FormInputControlType,
  FormInputNameType,
  FormInputRegisterType,
  SelectFormControlType,
} from '@/types'
import api from '@/utils/api/api'
import { generatePassword } from '@/utils/auth/generatePassword'
import handleFormSubmitError from '@/utils/forms/handleFormSubmitError'
import { denormalizePhoneForBackend } from '@/utils/phone/denormalizePhoneForBackend'

import Checkboxes from '../../Checkboxes/Checkboxes'
import {
  GENERAL_FIELDS,
  type LegalCreationType,
  physicalCreationSchema,
  type PhysicalCreationType,
} from '../Forms.config'
import cn from '../Forms.module.scss'
import {
  pickSharedValues,
  type RegisterFormHandle,
  SHARED_FIELD_NAMES,
} from '../lib/sharedFields'

const PhysicalForm = forwardRef<RegisterFormHandle>(
  function PhysicalForm(_, ref) {
    const {
      register,
      control,
      handleSubmit,
      setValue,
      getValues,
      getFieldState,
      clearErrors,
      watch,
      trigger,
      formState: { errors, isValid, dirtyFields },
    } = useForm<PhysicalCreationType>({
      shouldFocusError: false,
      resolver: zodResolver(physicalCreationSchema),
      mode: 'onChange',
    })

    useImperativeHandle(
      ref,
      () => ({
        getSharedValues: () => pickSharedValues(getValues()),
        setSharedValues: (values) => {
          const sync = { shouldDirty: true }
          if (values.surname !== undefined)
            setValue('surname', values.surname, sync)
          if (values.name !== undefined) setValue('name', values.name, sync)
          if (values.patronymic !== undefined)
            setValue('patronymic', values.patronymic, sync)
          if (values.phoneNumber !== undefined)
            setValue('phoneNumber', values.phoneNumber, sync)
          if (values.email !== undefined) setValue('email', values.email, sync)
          if (values.password !== undefined)
            setValue('password', values.password, sync)
          if (values.repeatPassword !== undefined)
            setValue('repeatPassword', values.repeatPassword, sync)
          if (values.paymentType !== undefined)
            setValue('paymentType', values.paymentType, sync)
          if (values.privacyPolicyAgreement !== undefined)
            setValue(
              'privacyPolicyAgreement',
              values.privacyPolicyAgreement,
              sync
            )
          const invalidFields = SHARED_FIELD_NAMES.filter(
            (name) => getFieldState(name).error
          )
          if (invalidFields.length > 0) trigger(invalidFields)
        },
      }),
      [getValues, setValue, getFieldState, trigger]
    )

    const password = watch('password')
    useEffect(() => {
      if (dirtyFields.repeatPassword) {
        trigger('repeatPassword')
      }
    }, [password])

    const [isSubmitting, setSubmitting] = useState(false)
    const paymentTypesQuery = useQuery(dictionariesQueries.paymentTypes())

    const [paymentSelectOpen, setPaymentSelectOpen] = useState(false)

    const { userId } = useUserInfoStore((state) => state)

    const navigate = useNavigate()
    const [searchParams] = useSearchParams()

    const [modal, contextHolder] = Modal.useModal()

    const onSubmit = (data: PhysicalCreationType) => {
      const searchParamsToken = searchParams.get('token')
      setSubmitting(true)

      const backendPhoneNumber = denormalizePhoneForBackend(data.phoneNumber)

      const surname = data.surname.trim()
      const name = data.name.trim()
      const patronymic = data.patronymic?.trim() || ''

      const fullDescription = [surname, name, patronymic]
        .filter(Boolean)
        .join(' ')

      const loadingToast = toast.loading('Регистрация...', {
        description: fullDescription,
      })

      const requestBody = {
        organisation: {
          description: fullDescription,
          phoneNumber: backendPhoneNumber,
          // Backend requires organisation.email in the body; the form field is optional,
          // so send an empty string instead of letting the key drop out of the request.
          email: data.email || '',
          contactPhone: backendPhoneNumber,
          // supervisorManager is an optional uuid — only send it when we actually have a
          // logged-in user id, never as an empty string (backend rejects "" as a uuid).
          ...(!searchParamsToken && userId && { supervisorManager: userId }),
        },
        user: {
          description: fullDescription,
          name: name,
          phoneNumber: backendPhoneNumber,
          // Backend requires user.email in the body; keep the key present even when blank.
          email: data.email || '',
          login: backendPhoneNumber,
          password: data.password,
          roles: ['USER'],
        },
        contractPaymentType: data.paymentType.value,
        bankAccounts: [],
        deliveryAddresses: [],
        contactInfos: [],
      }

      api
        .post(`/organisations/register`, requestBody, {
          params: {
            ...(searchParamsToken && { token: searchParamsToken }),
          },
          _skipSanitize: true,
        })
        .then(() => {
          toast.success('Регистрация успешна', {
            id: loadingToast,
            description: `${fullDescription} — ${backendPhoneNumber}`,
          })
          navigate('/register/success')
        })
        .catch((error) => {
          toast.dismiss(loadingToast)
          ModalMessage({
            title: 'Произошла ошибка при регистрации',
            modal,
            messageType: 'error',
            error,
          })
        })
        .finally(() => {
          setSubmitting(false)
        })
    }

    const { showPasswords, getVisibilityProps } = usePasswordVisibility()

    const generatePasswordСaller = () => {
      generatePassword(setValue, clearErrors as FormInputClearErrorsType)
      showPasswords()
    }

    return (
      <>
        {contextHolder}
        <form
          className={cn.form}
          onSubmit={handleSubmit(onSubmit, () =>
            handleFormSubmitError(isValid)
          )}
          autoComplete="off"
        >
          <div className={cn.inputsBlock}>
            {GENERAL_FIELDS.map((field) => {
              const { name, required, title } = field

              const isPasswordField =
                name === 'password' || name === 'repeatPassword'
              const isEmailField = name === 'email'

              return (
                <AtomFormInput
                  key={field.id}
                  location="register"
                  idPrefix="physical-"
                  layout="stacked"
                  title={title}
                  register={register as FormInputRegisterType}
                  name={name as FormInputNameType}
                  required={required}
                  autoComplete={isPasswordField ? 'new-password' : 'off'}
                  preventAutofill={isEmailField}
                  errors={name === 'email' || required ? errors : undefined}
                  control={control as FormInputControlType}
                  setValue={setValue}
                  readOnly={false}
                  generatePasswordCaller={
                    name === 'password' ? generatePasswordСaller : undefined
                  }
                  {...getVisibilityProps(name)}
                />
              )
            })}
            <HoverElement>
              <AtomSelect
                title="Способ оплаты"
                options={paymentTypesQuery.data?.map((paymentType) => ({
                  value: paymentType.id,
                  label: PAYMENT_TYPES[paymentType.paymentType],
                }))}
                required
                formControl={control as SelectFormControlType}
                layout="stacked"
                formErrors={errors}
                formName="paymentType"
                loading={paymentTypesQuery.isLoading}
                open={paymentSelectOpen}
                openByHover={false}
                onOpenChange={setPaymentSelectOpen}
                onChange={() => setPaymentSelectOpen(false)}
              />
            </HoverElement>
          </div>
          <Checkboxes
            control={
              control as unknown as Control<
                PhysicalCreationType | LegalCreationType
              >
            }
            errors={errors}
          />
          <div className={cn.submitButtonContainer}>
            <AtomButton
              fullWidth
              htmlType="submit"
              loading={isSubmitting}
              onClick={() => {
                if (
                  (!getValues('privacyPolicyAgreement') ||
                    !dirtyFields.privacyPolicyAgreement) &&
                  Object.keys(dirtyFields).length === GENERAL_FIELDS.length
                ) {
                  toast.error('Требуется согласие', {
                    description:
                      'Подтвердите согласие с Политикой конфиденциальности',
                  })
                  return
                }
                if (!isValid) {
                  toast.warning('Проверьте форму', {
                    description: 'Заполните все обязательные поля корректно',
                  })
                }
              }}
            >
              Зарегистрировать
            </AtomButton>
          </div>
        </form>
      </>
    )
  }
)

export default PhysicalForm
